import { type FC, useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { dayjsServer } from "@/helpers/dayjsServer"
import { openTgLink } from "@/helpers/openTgLink"
import { useServerTimer } from "@/hooks/useServerTimer"
import {
  useCancelMyFriendLobbyMutation,
  useGetMyFriendLobbyQuery
} from "@/store/apis/friendLobbyAPI"
import { Button } from "@/ui/Button"
import { Modal } from "@/ui/Modal"
import { OutlinedText } from "@/ui/OutlinedText"
import styles from "../../PlayWithFriendModal.module.scss"
import { InviteLinkRow } from "../InviteLinkRow"

export const CreatorView: FC<{ onClose: () => void }> = ({ onClose }) => {
  const { t } = useTranslation()

  const { data } = useGetMyFriendLobbyQuery(undefined)

  const [cancelMyFriendLobby, { isLoading: isCancelling }] =
    useCancelMyFriendLobbyMutation()

  const { minutes, seconds, restart } = useServerTimer({
    expiryTimestamp: new Date(),
    autoStart: false
  })

  const timer = useMemo(() => {
    if (minutes > 0) {
      return `${minutes}${t("minute_short")} ${seconds}${t("second_short")}`
    }
    return `${seconds}${t("second_short")}`
  }, [minutes, seconds, t])

  const inviteLink = useMemo(() => {
    if (!data?.inviteToken) return undefined
    return {
      tg: `https://t.me/${import.meta.env.VITE_BOTNAME}/${import.meta.env.VITE_WEBAPP_NAME}?startapp=friendInviteCode_${data.inviteToken}`,
      url: `${window.location.origin}/?friendInviteCode=${data.inviteToken}`
    }
  }, [data?.inviteToken])

  const handleShare = () => {
    if (!inviteLink) return

    const text = t("playWithMe")
    const linkToGame = encodeURIComponent(inviteLink.tg)
    const link = `${encodeURI(`https://t.me/share/url?text=${text}&url=`)}${linkToGame}`

    openTgLink(link)
  }

  const handleCancel = async () => {
    try {
      await cancelMyFriendLobby().unwrap()
      onClose()
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    if (!data?.expiresAt) return
    restart(dayjsServer(data.expiresAt).toDate())
  }, [data?.expiresAt, restart])

  return (
    <Modal.StoneFlexContainer
      className={styles.PlayWithFriendModal_container}
      content={{ className: styles.PlayWithFriendModal_content }}
      outside={
        <div className={styles.PlayWithFriendModal_footerButtons}>
          <Button
            className={styles.PlayWithFriendModal_footerButtons_item}
            styles="red"
            onClick={handleCancel}
            isLoading={isCancelling}
          >
            {t("button.cancel")}
          </Button>
        </div>
      }
    >
      <OutlinedText className={styles.PlayWithFriendModal_title}>
        {t("playWithFriend.title")}
      </OutlinedText>
      <div className={styles.PlayWithFriendModal_timer}>{timer}</div>
      <div className={styles.PlayWithFriendModal_waiting}>
        {t("friendLobby.waitingForFriend")}
      </div>
      <InviteLinkRow link={inviteLink?.url} onShare={handleShare} />
    </Modal.StoneFlexContainer>
  )
}
